import { mergeStreamText } from "./streamText";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useExchangeToken from "../../../hooks/useExchangeToken";
import { sendChatbotMessageStream } from "./chatbotApiStream";
import { mapChatbotError, parseChatbotStreamEvent, repairMojibake } from "./chatbotResponse";
import dedupeParagraphs from "./dedupeParagraphs";

const THINKING_LINE_PATTERNS = [
  /^planning the next steps/i,
  /^choosing data sources to use/i,
  /^getting additional context/i,
  /^running\s+/i,
  /^the results show:/i,
  /^this is interesting/i,
  /^now i have a good picture/i,
  /^reviewing the results/i,
  /^rethinking the plan/i,
  /^postprocessing and validating sql/i,
  /^executing sql/i,
  /^query id:/i,
  /^let me analyze/i,
  /^let me present/i,
  /^forming the answer/i,
  /^running system_execute_sql/i,
  /^running oee_analytics/i,
];

const THINKING_STEP_KEYWORDS = [
  "planning the next steps",
  "choosing data sources to use",
  "getting additional context",
  "running oee_analytics",
  "running system_execute_sql",
  "postprocessing and validating sql",
  "executing sql",
  "reviewing the results",
  "rethinking the plan",
  "forming the answer",
  "let me analyze",
  "let me present",
];

const THINKING_STEP_LABELS = {
  "planning the next steps": "กำลังวางแผนคำตอบ",
  "choosing data sources to use": "กำลังเลือกแหล่งข้อมูล",
  "getting additional context": "กำลังดึงบริบทเพิ่ม",
  "running oee_analytics": "กำลังวิเคราะห์ OEE",
  "running system_execute_sql": "กำลัง query ข้อมูล",
  "postprocessing and validating sql": "กำลังตรวจสอบผล SQL",
  "executing sql": "กำลังประมวลผล SQL",
  "reviewing the results": "กำลังทบทวนผลลัพธ์",
  "rethinking the plan": "กำลังปรับแผนคำตอบ",
  "forming the answer": "กำลังเรียบเรียงคำตอบ",
  "let me analyze": "กำลังวิเคราะห์ข้อมูล",
  "let me present": "กำลังจัดรูปแบบคำตอบ",
};

function extractThinkingStepLabel(text = "") {
  const lowered = String(text || "").toLowerCase();
  let latestKeyword = "";
  let latestIndex = -1;

  for (const keyword of THINKING_STEP_KEYWORDS) {
    const index = lowered.lastIndexOf(keyword);
    if (index > latestIndex) {
      latestIndex = index;
      latestKeyword = keyword;
    }
  }

  if (!latestKeyword) {
    return "";
  }

  return THINKING_STEP_LABELS[latestKeyword] || "กำลังประมวลผล";
}

function resolveThinkingStepLabel(parsedEvent) {
  const eventSource = `${parsedEvent?.eventName || ""} ${parsedEvent?.textChunk || ""}`.trim();
  const label = extractThinkingStepLabel(eventSource);

  if (label) {
    return label;
  }

  if (/(tool|status|trace|log)/i.test(parsedEvent?.eventName || "")) {
    return "กำลังประมวลผล";
  }

  if (/(plan|think|thinking)/i.test(parsedEvent?.eventName || "")) {
    return "กำลังวางแผนคำตอบ";
  }

  return "";
}

function isThinkingLine(line) {
  const trimmed = line.trim();
  if (!trimmed) {
    return false;
  }

  return THINKING_LINE_PATTERNS.some((pattern) => pattern.test(trimmed));
}

function sanitizeAssistantText(rawText) {
  const repairedText = repairMojibake(rawText || "")
    .replace(/â/g, "—")
    .replace(/â/g, "-")
    .replace(/ð/g, "🙂")
    .replace(/ð/g, "😊");

  const cleanedLines = repairedText
    .replace(/\r\n/g, "\n")
    .split("\n")
    .filter((line) => !isThinkingLine(line));

  const cleanedText = cleanedLines
    .join("\n")
    .replace(/(Reviewing the results|Forming the answer)/gi, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  const finalSegment = cleanedText;

  return dedupeParagraphs(finalSegment);
}

function buildPageContext(params, pathname) {
  const { plant, factory, line } = params;
  const segments = pathname.split("/").filter(Boolean);
  const oeeIndex = segments.findIndex((segment) => segment === "oee");
  const currentTab = oeeIndex >= 0 ? segments[oeeIndex + 1] : "production";
  const parsedFactory = Number(factory);

  const pageContext = {
    plant_id: plant || undefined,
    factory: Number.isNaN(parsedFactory) ? 0 : parsedFactory,
    line: line || undefined,
    page: currentTab ? `oee_${currentTab}` : undefined,
  };

  return Object.fromEntries(
    Object.entries(pageContext).filter(([, value]) => value !== undefined)
  );
}

export default function useChatbotStream() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const exchangeToken = useExchangeToken();
  const accessToken = useSelector((state) => state.auth.access_token);

  const [messages, setMessages] = useState([]);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [debugInfo, setDebugInfo] = useState(null);
  const [canRetry, setCanRetry] = useState(false);
  const [pendingSince, setPendingSince] = useState(null);
  const [thinkingStep, setThinkingStep] = useState("");
  const [liveAssistantMessage, setLiveAssistantMessage] = useState(null);
  const thinkingLogRef = useRef([]);
  const thinkingStartRef = useRef(null);
  const finalStreamTextRef = useRef("");
  const streamDoneRef = useRef(false);
  const finalResponseStartedRef = useRef(false);

  const abortRef = useRef(null);
  const lastRequestRef = useRef(null);
  const messagesRef = useRef([]);

  const pageContext = useMemo(
    () => buildPageContext(params, location.pathname),
    [location.pathname, params]
  );

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => () => {
    abortRef.current?.abort();
  }, []);

  const clearMessages = useCallback(() => {
    abortRef.current?.abort();
    setMessages([]);
    setError("");
    setDebugInfo(null);
    setCanRetry(false);
    setThinkingStep("");
    setLiveAssistantMessage(null);
    thinkingLogRef.current = [];
    thinkingStartRef.current = null;
    finalStreamTextRef.current = "";
    streamDoneRef.current = false;
    finalResponseStartedRef.current = false;
    lastRequestRef.current = null;
  }, []);

  const runChatRequest = useCallback(
    async ({ message, transcript, appendUserMessage }) => {
      const trimmed = message.trim();
      if (!trimmed) {
        return { ok: false, error: null };
      }

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const userMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        content: trimmed,
      };

      if (appendUserMessage) {
        setMessages((currentMessages) => [...currentMessages, userMessage]);
      }

      setPending(true);
      setPendingSince(Date.now());
      setError("");
      setCanRetry(false);
      setThinkingStep("กำลังวางแผนคำตอบ");
      thinkingLogRef.current = ["กำลังวางแผนคำตอบ"];
      thinkingStartRef.current = Date.now();
      finalStreamTextRef.current = "";
      streamDoneRef.current = false;
      finalResponseStartedRef.current = false;
      setLiveAssistantMessage({
        id: `assistant-live-${Date.now()}`,
        role: "assistant",
        mode: "thinking",
        content: "",
        thinkingSteps: ["กำลังวางแผนคำตอบ"],
        thinkingDurationSec: 0,
        statusText: "กำลังวางแผนคำตอบ",
      });

      lastRequestRef.current = {
        message: trimmed,
        transcript,
        pageContext,
      };

      let latestMetadata = null;

      const handleStreamEvent = (eventFrame) => {
        const parsedEvent = parseChatbotStreamEvent(eventFrame);

        if (parsedEvent.metadata) {
          latestMetadata = parsedEvent.metadata;
        }

        if (parsedEvent.code || parsedEvent.detail) {
          setDebugInfo((currentDebug) =>
            currentDebug || {
              upstream_status: parsedEvent.upstreamStatus,
              code: parsedEvent.code,
              detail: parsedEvent.detail,
            }
          );
        }

        if (parsedEvent.kind === "done" || parsedEvent.isDone) {
          streamDoneRef.current = true;
          return true;
        }

        if (parsedEvent.kind === "error") {
          return;
        }

        if (parsedEvent.kind === "thinking") {
          if (finalResponseStartedRef.current) {
            return;
          }

          const nextThinkingStep = resolveThinkingStepLabel(parsedEvent);

          if (nextThinkingStep) {
            setThinkingStep(nextThinkingStep);
            const log = thinkingLogRef.current;
            if (log[log.length - 1] !== nextThinkingStep) {
              thinkingLogRef.current = [...log, nextThinkingStep];
            }
          }

          setLiveAssistantMessage((currentMessage) => ({
            id: currentMessage?.id || `assistant-live-${Date.now()}`,
            role: "assistant",
            mode: "thinking",
            content: currentMessage?.content || "",
            thinkingSteps: thinkingLogRef.current.length > 0 ? [...thinkingLogRef.current] : ["กำลังวางแผนคำตอบ"],
            thinkingDurationSec: thinkingStartRef.current
              ? Math.round((Date.now() - thinkingStartRef.current) / 1000)
              : 0,
            statusText: nextThinkingStep || currentMessage?.statusText || "กำลังประมวลผล",
          }));
          return;
        }

        if (parsedEvent.kind === "final" && parsedEvent.textChunk) {
          finalResponseStartedRef.current = true;
          finalStreamTextRef.current = mergeStreamText(finalStreamTextRef.current, parsedEvent.textChunk, parsedEvent.textMode);
          const liveText = repairMojibake(finalStreamTextRef.current);

          setLiveAssistantMessage((currentMessage) => ({
            id: currentMessage?.id || `assistant-live-${Date.now()}`,
            role: "assistant",
            mode: "final",
            content: liveText,
            thinkingSteps: thinkingLogRef.current.length > 0 ? [...thinkingLogRef.current] : null,
            thinkingDurationSec: thinkingStartRef.current
              ? Math.round((Date.now() - thinkingStartRef.current) / 1000)
              : 0,
            statusText: currentMessage?.statusText || "",
          }));
          return;
        }

        if (parsedEvent.textChunk && !streamDoneRef.current) {
          finalStreamTextRef.current = mergeStreamText(finalStreamTextRef.current, parsedEvent.textChunk, parsedEvent.textMode);
        }
      };

      try {
        let token = accessToken;
        if (!token) {
          token = await exchangeToken();
        }

        if (!token) {
          throw new Error("missing_token");
        }

        try {
          await sendChatbotMessageStream({
            message: trimmed,
            transcript,
            pageContext,
            token,
            signal: controller.signal,
            onEvent: handleStreamEvent,
          });
        } catch (requestError) {
          if (requestError?.response?.status === 401) {
            const renewedToken = await exchangeToken({ force: true });

            if (!renewedToken) {
              throw requestError;
            }

            await sendChatbotMessageStream({
              message: trimmed,
              transcript,
              pageContext,
              token: renewedToken,
              signal: controller.signal,
              onEvent: handleStreamEvent,
            });
          } else {
            throw requestError;
          }
        }

        const aggregatedMessage = finalStreamTextRef.current;
        const sanitizedMessage = sanitizeAssistantText(aggregatedMessage);
        const finalizedMessage = sanitizedMessage || "ยังไม่มีข้อความตอบกลับจากระบบ";

        const thinkingDurationSec = thinkingStartRef.current
          ? Math.round((Date.now() - thinkingStartRef.current) / 1000)
          : 0;

        setMessages((currentMessages) => [
          ...currentMessages,
          {
            id: `assistant-${Date.now()}`,
            role: "assistant",
            content: finalizedMessage,
            metadata: latestMetadata,
            thinkingSteps: thinkingLogRef.current.length > 0 ? [...thinkingLogRef.current] : null,
            thinkingDurationSec,
          },
        ]);

        setDebugInfo(latestMetadata || null);
        setLiveAssistantMessage(null);

        return {
          ok: true,
          data: {
            assistantMessage: finalizedMessage,
            metadata: latestMetadata,
          },
        };
      } catch (requestError) {
        const fallbackText = sanitizeAssistantText(finalStreamTextRef.current || "").trim();
        if (fallbackText) {
          const thinkingDurationSec = thinkingStartRef.current
            ? Math.round((Date.now() - thinkingStartRef.current) / 1000)
            : 0;

          setMessages((currentMessages) => [
            ...currentMessages,
            {
              id: `assistant-${Date.now()}`,
              role: "assistant",
              content: fallbackText,
              metadata: latestMetadata,
              thinkingSteps: thinkingLogRef.current.length > 0 ? [...thinkingLogRef.current] : null,
              thinkingDurationSec,
            },
          ]);

          setDebugInfo(latestMetadata || null);
          setError("");
          setCanRetry(false);
          setLiveAssistantMessage(null);
          return {
            ok: true,
            data: {
              assistantMessage: fallbackText,
              metadata: latestMetadata,
            },
          };
        }

        if (requestError?.message === "missing_token") {
          setError("ไม่พบสิทธิ์การเข้าใช้งาน กรุณาเข้าสู่ระบบใหม่");
          navigate("/");
          return { ok: false, error: null };
        }

        const errorInfo = mapChatbotError(requestError);

        if (errorInfo.isAbort) {
          return { ok: false, aborted: true, error: errorInfo };
        }

        if (errorInfo.status === 401) {
          setError(errorInfo.message);
          navigate("/");
          return { ok: false, error: errorInfo };
        }

        setDebugInfo((currentDebug) => currentDebug || {
          upstream_status: errorInfo.upstreamStatus,
          code: errorInfo.code,
        });
        setError(errorInfo.message);
        setCanRetry(true);
        setLiveAssistantMessage(null);
        return { ok: false, error: errorInfo };
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
          setPending(false);
          setPendingSince(null);
          setThinkingStep("");
          finalResponseStartedRef.current = false;
          if (!streamDoneRef.current) {
            setLiveAssistantMessage(null);
          }
        }
      }
    },
    [accessToken, exchangeToken, navigate, pageContext]
  );

  const sendChat = useCallback(
    async (content) => {
      const trimmed = content.trim();
      if (!trimmed) {
        return;
      }

      const transcript = messagesRef.current.map(({ role, content: turnContent }) => ({
        role,
        content: turnContent,
      }));

      return runChatRequest({
        message: trimmed,
        transcript,
        appendUserMessage: true,
      });
    },
    [runChatRequest]
  );

  const retryLastMessage = useCallback(async () => {
    if (!lastRequestRef.current) {
      return;
    }

    return runChatRequest({
      message: lastRequestRef.current.message,
      transcript: lastRequestRef.current.transcript,
      appendUserMessage: false,
    });
  }, [runChatRequest]);

  return {
    messages,
    pending,
    pendingSince,
    liveAssistantMessage,
    thinkingStep,
    error,
    debugInfo,
    canRetry,
    clearMessages,
    pageContext,
    sendChat,
    retryLastMessage,
  };
}
